// The bounded, FULL-value row source for the Map view (T13). NEVER the chart sample (which caps at
// 1 000 PREVIEW rows). Inline result sets already hold full values → slice to `cap` synchronously (no
// effect). Windowed result sets fetch up to `cap` rows of ONLY `neededColumnIds` as FULL (untruncated)
// values through T26's bounded batch IPC, guarded by `expectedGeneration` (a stale generation is
// discarded; the effect re-runs when the generation changes) and disposed on unmount / source change.

import { useEffect, useMemo, useState } from 'react';
import type { QueryResultSet } from '../../../../types/queryExecution';
import { fetchFullColumnValues } from '../../common/services/resultCacheFullValues';

const EMPTY_ROWS: Record<string, unknown>[] = [];

/** Windowed source descriptor — present only when the result set is backed by a cache URI. */
export type WindowedRowSource = {
  cacheUri: string;
  /** Result-source generation (advances on sort/filter). Passed as `expectedGeneration`. */
  generation: number;
  /** Total rows in the windowed source (for the truthful banner). */
  totalRowCount: number;
};

export type SpatialResultRows = {
  rows: Record<string, unknown>[];
  /** Total rows in the source (windowed total, or the inline row count). */
  total: number;
  /** Rows actually available — may be < cap when the byte budget tripped (A12/G6+CD6). */
  rowsReturned: number;
  /** True when T26's `maxTotalBytes` stopped the fetch early. */
  byteBudgetExceeded: boolean;
  loading: boolean;
};

type FetchState = {
  rows: Record<string, unknown>[];
  rowsReturned: number;
  byteBudgetExceeded: boolean;
  loading: boolean;
};

export function useSpatialResultRows(params: {
  resultSet: QueryResultSet | undefined;
  neededColumnIds: string[];
  cap: number;
  windowed?: WindowedRowSource;
}): SpatialResultRows {
  const { resultSet, neededColumnIds, cap, windowed } = params;
  const neededKey = neededColumnIds.join(' ');
  const isWindowed = Boolean(windowed?.cacheUri);

  const inlineRows = useMemo(() => resultSet?.rows ?? EMPTY_ROWS, [resultSet]);
  const inlineSlice = useMemo(
    () => (inlineRows.length > cap ? inlineRows.slice(0, cap) : inlineRows),
    [inlineRows, cap],
  );

  const [fetchState, setFetchState] = useState<FetchState>({
    rows: [],
    rowsReturned: 0,
    byteBudgetExceeded: false,
    loading: true,
  });

  // Windowed ONLY. Inline mode is derived synchronously below and never triggers this effect (so an
  // unstable `resultSet` prop identity can never spin it). Deps are primitives + the needed-columns
  // key, so the effect fires exactly once per (uri, generation, cap, columns).
  useEffect(() => {
    if (!isWindowed || !windowed) return;
    let cancelled = false;
    setFetchState((s) => ({ ...s, loading: true }));
    void fetchFullColumnValues({
      uri: windowed.cacheUri,
      columnIds: neededColumnIds,
      startIndex: 0,
      count: cap,
      expectedGeneration: windowed.generation,
    }).then((resp) => {
      if (cancelled) return;
      // Stale generation → discard; the effect re-runs when `windowed.generation` advances.
      if (resp.generation !== windowed.generation) {
        setFetchState({ rows: [], rowsReturned: 0, byteBudgetExceeded: false, loading: false });
        return;
      }
      setFetchState({
        rows: resp.values,
        rowsReturned: resp.rowsReturned,
        byteBudgetExceeded: resp.byteBudgetExceeded,
        loading: false,
      });
    });
    return () => {
      cancelled = true;
    };
    // neededKey stands in for neededColumnIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWindowed, windowed?.cacheUri, windowed?.generation, cap, neededKey]);

  if (isWindowed && windowed) {
    return {
      rows: fetchState.rows,
      total: windowed.totalRowCount,
      rowsReturned: fetchState.rowsReturned,
      byteBudgetExceeded: fetchState.byteBudgetExceeded,
      loading: fetchState.loading,
    };
  }
  return {
    rows: inlineSlice,
    total: resultSet?.rowCount ?? inlineRows.length,
    rowsReturned: inlineSlice.length,
    byteBudgetExceeded: false,
    loading: false,
  };
}
